/**
 * Name-based deduplication for the cross-cutting entities that have no natural
 * key other than their name: labels, topics, calendars. These are seeded with
 * fresh random ids on every install, so two devices' "Work" topic (or a backup
 * imported onto an install that already seeded its own defaults) collide by
 * NAME while differing by id. Pure id-based merge (sync + `planMerge`) can never
 * collapse them, so they double. This module reconciles by name.
 *
 * Topics are keyed by (parent_id, name) so two same-named topics under different
 * parents stay distinct; labels and calendars are keyed by name alone.
 *
 * Pure — no I/O.
 */

export interface NamedEntity {
  id: string;
  name: string;
  parent_id?: string | null;
  created_at: string;
}

/** Normalized identity key: trimmed, case-folded name, scoped by parent. */
export function dedupKey(name: string, parentId?: string | null): string {
  return `${parentId ?? ""}::${name.trim().toLowerCase()}`;
}

/** Process parents before children so a child's key sees its remapped parent. */
function parentsFirst<T extends NamedEntity>(rows: T[]): T[] {
  return [...rows].sort((a, b) => (a.parent_id ? 1 : 0) - (b.parent_id ? 1 : 0));
}

/**
 * Decide which INCOMING entities to keep when merging them into an EXISTING set:
 * an incoming entity whose name collides with an existing one (or with an
 * already-kept incoming one) is dropped, and `remap` records its id → the
 * canonical id to use in its place. Kept entities whose parent was remapped get
 * their `parent_id` rewritten. Existing rows always win (their id is canonical).
 */
export function reconcileIncoming<T extends NamedEntity>(
  existing: T[],
  incoming: T[],
): { keep: T[]; remap: Map<string, string> } {
  const existingIds = new Set(existing.map((e) => e.id));
  const canonByKey = new Map<string, string>();
  for (const e of existing) {
    const k = dedupKey(e.name, e.parent_id);
    if (!canonByKey.has(k)) canonByKey.set(k, e.id);
  }

  const remap = new Map<string, string>();
  const keep: T[] = [];
  for (const inc of parentsFirst(incoming)) {
    if (existingIds.has(inc.id)) continue; // already present locally — existing wins
    const parentId =
      inc.parent_id != null ? remap.get(inc.parent_id) ?? inc.parent_id : inc.parent_id;
    const k = dedupKey(inc.name, parentId);
    const canon = canonByKey.get(k);
    if (canon) {
      if (canon !== inc.id) remap.set(inc.id, canon);
      continue;
    }
    canonByKey.set(k, inc.id);
    keep.push(parentId === inc.parent_id ? inc : { ...inc, parent_id: parentId });
  }
  return { keep, remap };
}

/**
 * Build a loser-id → winner-id remap that collapses same-name duplicates WITHIN
 * one set (used to heal an install that already accumulated duplicates, e.g. via
 * sync). The winner is the lexicographically smallest id so every device that
 * sees the same set picks the SAME survivor — without that determinism, two
 * devices could each tombstone the other's copy and the entity would vanish.
 */
export function buildNameRemap<T extends NamedEntity>(rows: T[]): Map<string, string> {
  const winnerByKey = new Map<string, string>();
  for (const r of rows) {
    const k = dedupKey(r.name, r.parent_id);
    const cur = winnerByKey.get(k);
    if (!cur || r.id < cur) winnerByKey.set(k, r.id);
  }
  const remap = new Map<string, string>();
  for (const r of rows) {
    const winner = winnerByKey.get(dedupKey(r.name, r.parent_id))!;
    if (winner !== r.id) remap.set(r.id, winner);
  }
  return remap;
}

/** Remap a list of entity ids through a loser→winner map, de-duplicating and
 *  preserving order. Returns the same array reference when nothing changed. */
export function remapIds(ids: string[], remap: Map<string, string>): string[] {
  if (!remap.size || !ids.some((id) => remap.has(id))) return ids;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    const next = remap.get(id) ?? id;
    if (!seen.has(next)) {
      seen.add(next);
      out.push(next);
    }
  }
  return out;
}
